import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaginationBar } from "@/components/pagination-bar";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Mail, User, UserPlus, X, Check } from "lucide-react";
import { cn } from "@/lib/cn";

function relativeTime(dateStr: string) {
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString();
}

/** Parse "John Smith <john@example.com>" or "john@example.com" */
function parseFrom(fromLine: string): { name: string; email: string } {
  const match = fromLine.match(/^(.*?)\s*<(.+?)>$/);
  if (match) return { name: match[1].trim(), email: match[2].trim().toLowerCase() };
  const emailOnly = fromLine.trim().toLowerCase();
  return { name: emailOnly.split("@")[0] ?? "", email: emailOnly };
}

interface CreateLeadModal {
  name: string;
  email: string;
  subject: string;
}

export default function SharedInboxPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [q, setQ]       = useState("");
  const pageSize = 25;

  // Create Lead modal state
  const [createLead, setCreateLead] = useState<CreateLeadModal | null>(null);
  const [leadName,    setLeadName]    = useState("");
  const [leadEmail,   setLeadEmail]   = useState("");
  const [leadCompany, setLeadCompany] = useState("");
  const [leadTitle,   setLeadTitle]   = useState("");
  const [leadDone,    setLeadDone]    = useState<string | null>(null); // id of created lead

  function openCreateLead(fromLine: string, subject: string) {
    const { name, email } = parseFrom(fromLine);
    setLeadName(name);
    setLeadEmail(email);
    setLeadCompany("");
    setLeadTitle("");
    setLeadDone(null);
    setCreateLead({ name, email, subject });
  }

  const createLeadMut = useMutation({
    mutationFn: () =>
      api.post("/leads", {
        name:    leadName || leadEmail,
        email:   leadEmail,
        company: leadCompany || undefined,
        title:   leadTitle  || undefined,
        source:  "Shared Inbox",
        notes:   `Created from shared inbox email: "${createLead?.subject}"`,
      }).then((r) => r.data),
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: ["/leads"] });
      setLeadDone(lead.id);
    },
  });

  // Check if the user's Microsoft token is expired (needed to sync shared inbox)
  const { data: graphStatus } = useQuery({
    queryKey: ["/graph/status"],
    queryFn: () => api.get("/graph/status").then((r) => r.data as { emailConnected: boolean; emailExpired: boolean }),
    staleTime: 60_000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["shared-inbox", page, q],
    queryFn: () =>
      api.get("/activities", {
        params: {
          source: "shared_inbox",
          page,
          pageSize,
          sortBy: "completedAt",
          sortDir: "desc",
          ...(q ? { q } : {}),
        },
      }).then((r) => r.data),
  });

  const rows  = data?.data ?? [];
  const total = data?.pagination?.total ?? data?.total ?? 0;

  const sync = useMutation<any, any, boolean>({
    mutationFn: (full: boolean) =>
      api.post(`/graph/sync-shared-inbox${full ? "?full=true" : ""}`).then((r) => r.data),
    onSuccess: (result, full) => {
      qc.invalidateQueries({ queryKey: ["shared-inbox"] });
      if (full) {
        alert(result.message ?? "Full historical sync started. Refresh in a minute.");
      } else {
        alert(`Synced ${result.synced ?? 0} new email(s).`);
      }
    },
    onError: (err: any) => {
      alert(err?.response?.data?.message ?? "Sync failed — make sure your email is connected in My Profile.");
    },
  });

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Shared Inbox"
        description="Emails received at sales@cbcinc.ai — visible to all team members."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => sync.mutate(true)} disabled={sync.isPending}>
              <RefreshCw className={cn("h-4 w-4 mr-1.5", sync.isPending && "animate-spin")} />
              Sync all history
            </Button>
            <Button variant="outline" size="sm" onClick={() => sync.mutate(false)} disabled={sync.isPending}>
              <RefreshCw className={cn("h-4 w-4 mr-1.5", sync.isPending && "animate-spin")} />
              Sync recent
            </Button>
          </div>
        }
      />

      {/* Token expired banner */}
      {graphStatus?.emailExpired && (
        <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-center gap-2">
          <span className="font-medium">⚠️ Microsoft token expired.</span>
          New emails won't sync until you{" "}
          <a href="/login" className="font-medium underline">sign in again</a>.
        </div>
      )}

      <div className="px-4 py-2 border-b bg-card flex items-center gap-2">
        <Input
          placeholder="Search subject or body…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          className="h-8 w-72"
        />
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="p-12 text-center text-muted-foreground">Loading…</div>
        )}
        {!isLoading && rows.length === 0 && (
          <div className="p-12 text-center">
            <Mail className="h-8 w-8 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-muted-foreground text-sm">No emails in the shared inbox yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click "Sync now" to pull emails from sales@cbcinc.ai.
            </p>
          </div>
        )}
        <ul className="divide-y">
          {rows.map((item: any) => {
            const lines = (item.body ?? "").split("\n");
            const fromLine = lines.find((l: string) => l.startsWith("From:")) ?? "";
            const fromRaw  = fromLine.replace("From: ", "").trim();
            const preview  = lines.filter((l: string) => !l.startsWith("From:")).join(" ").slice(0, 140);
            const hasContact = !!item.contact;
            return (
              <li key={item.id} className="px-4 py-3 hover:bg-muted/40 transition-colors">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Mail className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">{item.subject}</span>
                        {hasContact && (
                          <Badge variant="secondary" className="text-xs shrink-0">
                            <User className="h-2.5 w-2.5 mr-1" />{item.contact.name}
                          </Badge>
                        )}
                      </div>
                      {fromRaw && (
                        <p className="text-xs text-muted-foreground mt-0.5">{fromRaw}</p>
                      )}
                      {preview && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{preview}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground mt-0.5">
                      {item.completedAt ? relativeTime(item.completedAt) : ""}
                    </span>
                    {fromRaw && !hasContact && (
                      <button
                        onClick={() => openCreateLead(fromRaw, item.subject ?? "")}
                        className="flex items-center gap-1 rounded border border-dashed border-primary/40 px-2 py-0.5 text-xs text-primary hover:bg-primary/10 transition-colors"
                        title="Create Lead from this email"
                      >
                        <UserPlus className="h-3 w-3" />
                        Create Lead
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Create Lead modal */}
        {createLead && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold">Create Lead from Email</h2>
                <button onClick={() => setCreateLead(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {leadDone ? (
                <div className="flex flex-col items-center gap-3 py-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <Check className="h-6 w-6 text-green-600" />
                  </div>
                  <p className="font-medium">Lead created!</p>
                  <p className="text-sm text-muted-foreground">{leadName || leadEmail} has been added to your Leads.</p>
                  <div className="flex gap-2 mt-2">
                    <Button variant="outline" size="sm" onClick={() => setCreateLead(null)}>Close</Button>
                    <Button size="sm" asChild>
                      <a href={`/leads/${leadDone}`} target="_blank" rel="noreferrer">View Lead</a>
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Name</Label>
                      <Input value={leadName} onChange={(e) => setLeadName(e.target.value)} className="mt-1 h-8 text-sm" placeholder="Full name" />
                    </div>
                    <div>
                      <Label className="text-xs">Email</Label>
                      <Input value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} className="mt-1 h-8 text-sm" placeholder="email@example.com" />
                    </div>
                    <div>
                      <Label className="text-xs">Company <span className="text-muted-foreground">(optional)</span></Label>
                      <Input value={leadCompany} onChange={(e) => setLeadCompany(e.target.value)} className="mt-1 h-8 text-sm" placeholder="Company name" />
                    </div>
                    <div>
                      <Label className="text-xs">Title <span className="text-muted-foreground">(optional)</span></Label>
                      <Input value={leadTitle} onChange={(e) => setLeadTitle(e.target.value)} className="mt-1 h-8 text-sm" placeholder="Job title" />
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Source will be set to "Shared Inbox". The email subject will be saved as a note.
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 mt-5">
                    <Button variant="outline" size="sm" onClick={() => setCreateLead(null)}>Cancel</Button>
                    <Button
                      size="sm"
                      onClick={() => createLeadMut.mutate()}
                      disabled={createLeadMut.isPending || !leadEmail}
                    >
                      {createLeadMut.isPending ? "Creating…" : "Create Lead"}
                    </Button>
                  </div>
                  {createLeadMut.isError && (
                    <p className="text-xs text-destructive mt-2">
                      {(createLeadMut.error as any)?.response?.data?.message ?? "Failed to create lead."}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
    </div>
  );
}
